import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import enhancementsImage from "assets/enhancements.svg";
import BattleRating from "components/base/BattleRating";
import Dialogs from "components/base/Dialogs";
import { ItemText } from "components/base/ItemBase";
import ItemIcon from "components/base/ItemIcon";
import ItemName from "components/base/ItemName";
import {
  DialogWrapper,
  FightOverlayActions,
  ItemImageWrapper,
  ItemInfo,
  ItemTitle,
  ItemWrapper,
  ListItem,
  OverlayBox,
  OverlayButton,
  OverlayColumn,
  OverlayColumns,
  OverlayList,
  OverlayStatement,
  OverlaySubtitle,
  OverlayTitle,
} from "components/base/OverlayBase";
import RatingProgress from "components/base/RatingProgress";
import Tooltip, { TooltipDesc, TooltipName } from "components/base/Tooltip";
import TooltipMedallions from "components/base/TooltipMedallions";
import useDialog from "components/hooks/useDialog";
import { getMobOverlayDialog } from "libs/fight";
import { getAreaData, getPlanetData } from "libs/stats";
import { fightAgain, onwardFightLose, onwardFightWin } from "redux/actions";
import { getLocationInfo } from "redux/selectors/characterSelectors";
import {
  getFightResults,
  getFightStatus,
  getOpponentBaseInfo,
  getOpponentDialogs,
} from "redux/selectors/fightSelectors";
import { COLORS } from "utils/constants";

interface GainImageProps {
  image: string;
}

const CurrentRatingWrapper = styled.div`
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RatingWrapper = styled.div`
  display: inline-block;
`;

const ProgressWrapper = styled.div`
  margin-top: 10px;
  width: 100%;
  padding: 12px 20px;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: #43fffa10;
  border: 1px #43fffa10 solid;
`;

const RewardsWrapper = styled.div`
  margin-bottom: 15px;
`;

const EnhancementsImageWrapper = styled(ItemImageWrapper)`
  background-color: ${COLORS.ENHANCEMENTS}50;
`;

const GainImage = styled.div<GainImageProps>`
  width: 100%;
  height: 100%;
  mask: url(${(props) => props.image}) no-repeat center;
  mask-size: 60%;
`;

const EnhancementsImage = styled(GainImage)`
  background-color: ${COLORS.ENHANCEMENTS};
`;

export default function FightResults() {
  const dispatch = useDispatch();

  const { planet } = useSelector(getLocationInfo);

  const status = useSelector(getFightStatus);
  const opponentDialogs = useSelector(getOpponentDialogs);
  const {
    name: opponentName,
    images: opponentImages,
    isTutorial: isTutorialOpponent,
  } = useSelector(getOpponentBaseInfo);
  const results = useSelector(getFightResults);

  const activeDialog = getMobOverlayDialog(opponentDialogs, status);

  const { currentStep, currentDialog, hasAction } = useDialog(
    activeDialog || [],
    true
  );

  const currentPlanetInfo = getPlanetData(planet);
  const repairAreaInfo = getAreaData(currentPlanetInfo.mainArea);

  return (
    <OverlayBox>
      {status === "winResults" && (
        <>
          <OverlayTitle>You defeated {opponentName}!</OverlayTitle>
          {!!isTutorialOpponent && (
            <DialogWrapper>
              <Dialogs
                avatar={opponentImages.pilot}
                currentStep={currentStep}
                currentDialog={currentDialog}
                hasAction={hasAction}
              />
            </DialogWrapper>
          )}
          <CurrentRatingWrapper>
            <OverlaySubtitle>Battle Rating</OverlaySubtitle>
            <RatingWrapper>
              <BattleRating rating={results.battleRating} />
            </RatingWrapper>
            <ProgressWrapper>
              <RatingProgress isFinal />
            </ProgressWrapper>
          </CurrentRatingWrapper>
          <RewardsWrapper>
            <OverlaySubtitle>Rewards Unlocked</OverlaySubtitle>
            <OverlayColumns>
              <OverlayColumn>
                <OverlayList>
                  {results.drops.map((drop) => (
                    <ListItem key={drop}>
                      <ItemWrapper>
                        <ItemImageWrapper>
                          <ItemIcon slug={drop} />
                        </ItemImageWrapper>
                        <Tooltip
                          content={
                            <>
                              <TooltipName>New Upgrade Unlocked</TooltipName>
                              <TooltipDesc>
                                You've unlocked this new upgrade! Go to the
                                nearest Loadout Hub to install it.
                              </TooltipDesc>
                            </>
                          }
                        >
                          <ItemTitle>
                            <ItemName slug={drop} />
                          </ItemTitle>
                        </Tooltip>
                      </ItemWrapper>
                    </ListItem>
                  ))}
                </OverlayList>
              </OverlayColumn>
              <OverlayColumn>
                <OverlayList>
                  <ListItem>
                    <ItemWrapper>
                      <Tooltip
                        color={COLORS.ENHANCEMENTS}
                        content={<TooltipMedallions />}
                      >
                        <EnhancementsImageWrapper>
                          <EnhancementsImage image={enhancementsImage} />
                        </EnhancementsImageWrapper>
                      </Tooltip>
                      <ItemTitle>
                        <ItemText color={COLORS.ENHANCEMENTS}>
                          Medallions
                        </ItemText>
                      </ItemTitle>
                    </ItemWrapper>
                    <ItemInfo>{results.medallions}</ItemInfo>
                  </ListItem>
                </OverlayList>
              </OverlayColumn>
            </OverlayColumns>
          </RewardsWrapper>
          <FightOverlayActions>
            <OverlayButton
              $style="normal"
              $size="large"
              onClick={() => dispatch(onwardFightWin())}
            >
              Onward
            </OverlayButton>
            <OverlayButton
              $style="important"
              $size="large"
              onClick={() => dispatch(fightAgain())}
            >
              Battle Again
            </OverlayButton>
          </FightOverlayActions>
        </>
      )}
      {status === "loseResults" && (
        <>
          <OverlayTitle>You were defeated by {opponentName}.</OverlayTitle>
          {!!isTutorialOpponent && (
            <DialogWrapper>
              <Dialogs
                avatar={opponentImages.pilot}
                currentStep={currentStep}
                currentDialog={currentDialog}
                hasAction={hasAction}
              />
            </DialogWrapper>
          )}
          {!isTutorialOpponent && (
            <OverlayStatement>
              Your ship will be sent back to {repairAreaInfo.name} for full
              repairs.
            </OverlayStatement>
          )}
          <FightOverlayActions>
            <OverlayButton
              $style="normal"
              $size="large"
              onClick={() => dispatch(onwardFightLose())}
            >
              Onward
            </OverlayButton>
          </FightOverlayActions>
        </>
      )}
    </OverlayBox>
  );
}
